import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Shield, Plus, Loader2, Building2, Users, RefreshCw, CheckCircle2,
  XCircle, Clock, Mail, Calendar, ChevronDown, ChevronRight,
  Edit2, Trash2, Eye, EyeOff, UserPlus, Settings,
  TrendingUp, CheckSquare, ShoppingCart, BarChart2, FileDown, Target,
  Warehouse, Factory,
} from 'lucide-react';
import { clearModuleCache } from '../components/ModuleGate';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Amministratore' },
  { value: 'sales_manager', label: 'Resp. Commerciale' },
  { value: 'sales_rep', label: 'Commerciale' },
  { value: 'operator', label: 'Operatore' },
  { value: 'client', label: 'Cliente (Readonly)' },
];

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  sales_manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  sales_rep: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  operator: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  client: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

function UserAvatar({ name, size = 'md' }) {
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  const sz = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-8 h-8 text-xs';
  return (
    <div className={`${sz} rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0`}>
      <span className="font-bold text-primary">{initials}</span>
    </div>
  );
}

// ── Tenant Modules Section ────────────────────────────────────────────────────
const MODULE_DEFAULTS = {
  pipeline:    true,
  calendar:    true,
  activities:  true,
  orders:      true,
  analytics:   true,
  reports:     true,
  forecast:    false,
  inventory:   false,
  production:  false,
};

const KNOWN_MODULES = [
  { key: 'pipeline',    label: 'Pipeline',          desc: 'Opportunità e trattative commerciali',      icon: TrendingUp  },
  { key: 'calendar',    label: 'Calendario',         desc: 'Calendario eventi, visite e appuntamenti',  icon: Calendar    },
  { key: 'activities',  label: 'Attività',            desc: 'Task e to-do del team commerciale',         icon: CheckSquare },
  { key: 'orders',      label: 'Ordini & DDT',        desc: 'Ordini, documenti di trasporto, fatture',   icon: ShoppingCart},
  { key: 'analytics',   label: 'Analisi',             desc: 'Analytics ordini e performance vendite',    icon: BarChart2   },
  { key: 'reports',     label: 'Report',              desc: 'Generazione report PDF ed Excel',           icon: FileDown    },
  { key: 'forecast',    label: 'Forecast & Budget',   desc: 'Budget commerciale per azienda/prodotto',   icon: Target      },
  { key: 'inventory',   label: 'Scorte',              desc: 'Gestione materiali, movimenti e scorte',    icon: Warehouse   },
  { key: 'production',  label: 'Produzione',          desc: 'Distinta base, piani e fabbisogni MRP',     icon: Factory     },
];

function TenantModulesSection({ tenantId, initialModules }) {
  const [modules, setModules] = useState(initialModules || {});
  const [saving, setSaving] = useState({});

  const isEnabled = (key) =>
    modules[key] !== undefined ? modules[key] === true : MODULE_DEFAULTS[key] === true;

  const toggle = async (mod) => {
    setSaving(s => ({ ...s, [mod.key]: true }));
    try {
      const res = await axios.patch(`${API}/admin/tenants/${tenantId}/modules/${mod.key}`);
      setModules(m => ({ ...m, [mod.key]: res.data.enabled }));
      clearModuleCache(); // force re-check on next page navigation
      toast.success(`"${mod.label}" ${res.data.enabled ? 'abilitato' : 'disabilitato'}`);
    } catch { toast.error('Errore aggiornamento modulo'); }
    finally { setSaving(s => ({ ...s, [mod.key]: false })); }
  };

  return (
    <div className="px-4 py-4 border-t" style={{ background: 'hsl(var(--primary)/0.04)' }}>
      <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3">
        Moduli
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {KNOWN_MODULES.map(m => {
          const on = isEnabled(m.key);
          const Icon = m.icon;
          return (
            <div
              key={m.key}
              className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-all
                ${on
                  ? 'bg-background border-border'
                  : 'bg-muted/20 border-dashed border-muted-foreground/20 opacity-60'
                }`}
            >
              <Switch
                checked={on}
                onCheckedChange={() => toggle(m)}
                disabled={saving[m.key]}
                data-testid={`toggle-module-${tenantId}-${m.key}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <Icon size={11} className={on ? 'text-primary' : 'text-muted-foreground/40'} />
                  <p className="text-xs font-medium leading-tight truncate">{m.label}</p>
                </div>
                <p className={`text-[10px] mt-0.5 leading-tight ${on ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground/60'}`}>
                  {saving[m.key] ? '...' : on ? 'Attivo' : 'Non attivo'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Tenant Users Panel (inline, loads on expand) ──────────────────────────────
function TenantUsersPanel({ tenantId, tenantName }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [addForm, setAddForm] = useState({ name: '', email: '', role: 'admin', password: '' });
  const [editForm, setEditForm] = useState({ name: '', role: 'sales_rep' });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tenants/${tenantId}/users`);
      setUsers(res.data);
    } catch {
      toast.error('Errore caricamento utenti');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/tenants/${tenantId}/users`, addForm);
      setCreatedCreds({ ...res.data, password: addForm.password });
      setShowAddUser(false);
      setAddForm({ name: '', email: '', role: 'admin', password: '' });
      load();
      toast.success('Utente creato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tenants/${tenantId}/users/${editUser.id}`, editForm);
      toast.success('Utente aggiornato');
      setEditUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const toggleActive = async (uid, isActive) => {
    try {
      await axios.put(`${API}/admin/tenants/${tenantId}/users/${uid}`, { is_active: !isActive });
      toast.success(isActive ? 'Disabilitato' : 'Abilitato');
      load();
    } catch { toast.error('Errore'); }
  };

  const deleteUser = async (uid, name) => {
    if (!window.confirm(`Eliminare "${name}" da ${tenantName}?`)) return;
    try {
      await axios.delete(`${API}/admin/tenants/${tenantId}/users/${uid}`);
      toast.success('Utente eliminato');
      load();
    } catch { toast.error('Errore'); }
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="animate-spin text-primary" size={18} /></div>;

  return (
    <div className="px-4 pb-4 pt-2 border-t bg-muted/20">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Utenti ({users.length})
        </p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAddUser(true)}
          data-testid={`add-user-tenant-${tenantId}`}>
          <UserPlus size={12} className="mr-1" />Aggiungi utente
        </Button>
      </div>

      {users.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">Nessun utente in questo tenant</p>
      ) : (
        <div className="space-y-1">
          {users.map(u => (
            <div key={u.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors">
              <UserAvatar name={u.name} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-xs font-medium">{u.name}</p>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${ROLE_COLORS[u.role] || ROLE_COLORS.client}`}>
                    {ROLES.find(r => r.value === u.role)?.label || u.role}
                  </span>
                  {!u.is_active && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">Disabilitato</span>}
                </div>
                <p className="text-[10px] text-muted-foreground">{u.email}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={() => { setEditForm({ name: u.name, role: u.role }); setEditUser(u); }}
                  data-testid={`edit-tenant-user-${u.id}`}>
                  <Edit2 size={11} />
                </Button>
                <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u.id, u.is_active)}
                  className="scale-75" data-testid={`toggle-tenant-user-${u.id}`} />
                <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive/70 hover:text-destructive"
                  onClick={() => deleteUser(u.id, u.name)} data-testid={`delete-tenant-user-${u.id}`}>
                  <Trash2 size={11} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add user dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Aggiungi utente a {tenantName}</DialogTitle></DialogHeader>
          <form onSubmit={createUser} className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Nome *</Label>
              <Input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ruolo</Label>
              <Select value={addForm.role} onValueChange={v => setAddForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Password *</Label>
              <div className="relative">
                <Input type={showPwd ? 'text' : 'password'} value={addForm.password}
                  onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} required />
                <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPwd ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => setShowAddUser(false)}>Annulla</Button>
              <Button type="submit" size="sm" className="flex-1" disabled={saving}>
                {saving && <Loader2 size={12} className="mr-1 animate-spin" />}Crea
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Modifica utente</DialogTitle></DialogHeader>
          {editUser && (
            <form onSubmit={saveEdit} className="space-y-3 mt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Nome *</Label>
                <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Ruolo</Label>
                <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => setEditUser(null)}>Annulla</Button>
                <Button type="submit" size="sm" className="flex-1" disabled={saving}>
                  {saving && <Loader2 size={12} className="mr-1 animate-spin" />}Salva
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Created credentials dialog */}
      <Dialog open={!!createdCreds} onOpenChange={() => setCreatedCreds(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Utente creato</DialogTitle></DialogHeader>
          {createdCreds && (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/40 p-3 space-y-2 font-mono text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground text-xs">Email:</span><span className="font-medium text-xs">{createdCreds.email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground text-xs">Password:</span><span className="font-medium text-xs">{createdCreds.password}</span></div>
              </div>
              <Button className="w-full" size="sm" onClick={() => setCreatedCreds(null)}>Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Main SuperAdmin Component ─────────────────────────────────────────────────
export default function SuperAdmin() {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const [form, setForm] = useState({
    name: '', slug: '', plan: 'free',
    admin_name: '', admin_email: '', admin_password: '',
  });

  const fetchData = async () => {
    try {
      const [tenantsRes, statsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/tenants`),
        axios.get(`${API}/admin/platform-stats`),
        axios.get(`${API}/admin/pending-registrations`),
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
      setPending(pendingRes.data);
    } catch {
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const createTenant = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/tenants`, form);
      if (res.data.admin_created) {
        toast.success(`Tenant "${form.name}" creato con admin "${res.data.admin_created.name}"`);
      } else {
        toast.success('Tenant creato');
      }
      setShowForm(false);
      setForm({ name: '', slug: '', plan: 'free', admin_name: '', admin_email: '', admin_password: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const toggleTenant = async (id) => {
    try {
      const res = await axios.patch(`${API}/admin/tenants/${id}/toggle`);
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const approveRequest = async (id, name) => {
    if (!window.confirm(`Approvare la richiesta di "${name}"? Verrà creato il tenant e l'account admin.`)) return;
    setProcessingId(id);
    try {
      const res = await axios.post(`${API}/admin/pending-registrations/${id}/approve`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setProcessingId(null); }
  };

  const rejectRequest = async (id, name) => {
    if (!window.confirm(`Rifiutare la richiesta di "${name}"?`)) return;
    setProcessingId(id);
    try {
      await axios.delete(`${API}/admin/pending-registrations/${id}`);
      toast.success('Richiesta rifiutata');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setProcessingId(null); }
  };

  const setField = (k, v) => {
    setForm(f => {
      const updated = { ...f, [k]: v };
      if (k === 'name') updated.slug = v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return updated;
    });
  };

  const toggleExpand = (id) => setExpandedTenant(prev => prev === id ? null : id);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Super Admin Panel</h1>
            <p className="text-muted-foreground text-sm">Gestione piattaforma globale</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-admin">
            <RefreshCw size={14} className="mr-1.5" />Aggiorna
          </Button>
          <Button onClick={() => setShowForm(true)} data-testid="create-tenant-btn">
            <Plus size={16} className="mr-2" />Nuovo Tenant
          </Button>
        </div>
      </div>

      {/* Platform stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'Tenant', value: stats.tenants, icon: Shield },
            { label: 'Utenti', value: stats.users, icon: Users },
            { label: 'Aziende', value: stats.companies, icon: Building2 },
            { label: 'Contatti', value: stats.contacts, icon: Users },
            { label: 'Opportunità', value: stats.opportunities, icon: Building2 },
            { label: 'In attesa', value: stats.pending_registrations || 0, icon: Clock, alert: (stats.pending_registrations || 0) > 0 },
          ].map(({ label, value, icon: Icon, alert }) => (
            <Card key={label} className={alert && value > 0 ? 'border-amber-400 dark:border-amber-500' : ''}>
              <CardContent className="p-3 flex items-center gap-2">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${alert && value > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-primary/10'}`}>
                  <Icon size={13} className={alert && value > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-primary'} />
                </div>
                <div>
                  <p className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
                  <p className="text-[11px] text-muted-foreground">{label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="dashboard">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="tenants" data-testid="tab-tenants">Tenant ({tenants.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Richieste in attesa
            {pending.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                {pending.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── DASHBOARD TAB ── */}
        <TabsContent value="dashboard" className="mt-4">
          <div className="space-y-5">
            {/* Per-tenant overview table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Panoramica Tenant &amp; Moduli
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 border-b text-muted-foreground font-semibold">
                        <th className="text-left px-4 py-2.5 min-w-[160px]">Tenant</th>
                        <th className="text-center px-2 py-2.5">Utenti</th>
                        <th className="text-center px-2 py-2.5">Aziende</th>
                        {KNOWN_MODULES.map(m => (
                          <th key={m.key} className="text-center px-2 py-2.5 min-w-[70px]">
                            <div className="flex flex-col items-center gap-0.5">
                              <m.icon size={11} />
                              <span className="text-[9px] leading-tight">{m.label.split(' ')[0]}</span>
                            </div>
                          </th>
                        ))}
                        <th className="text-center px-3 py-2.5">Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map(t => {
                        const mods = t.modules || {};
                        const isOn = (key) => mods[key] !== undefined ? mods[key] : MODULE_DEFAULTS[key];
                        const activeCount = KNOWN_MODULES.filter(m => isOn(m.key)).length;
                        return (
                          <tr key={t._id || t.id} className="border-b hover:bg-muted/10 transition-colors">
                            <td className="px-4 py-2.5">
                              <p className="font-semibold">{t.name}</p>
                              <p className="text-muted-foreground font-mono text-[10px]">{t.slug}</p>
                            </td>
                            <td className="px-2 py-2.5 text-center font-medium">{t.users_count || 0}</td>
                            <td className="px-2 py-2.5 text-center font-medium">{t.companies_count || 0}</td>
                            {KNOWN_MODULES.map(m => (
                              <td key={m.key} className="px-2 py-2.5 text-center">
                                {isOn(m.key)
                                  ? <CheckCircle2 size={13} className="inline text-green-500" />
                                  : <XCircle size={13} className="inline text-muted-foreground/30" />
                                }
                              </td>
                            ))}
                            <td className="px-3 py-2.5 text-center">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full
                                ${t.is_active
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                }`}>
                                {t.is_active ? 'Attivo' : 'Sospeso'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {tenants.length === 0 && (
                        <tr>
                          <td colSpan={4 + KNOWN_MODULES.length} className="text-center py-10 text-muted-foreground">
                            Nessun tenant
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Module adoption summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Adozione Moduli (tenant attivi)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  {KNOWN_MODULES.map(m => {
                    const activeTenants = tenants.filter(t => t.is_active);
                    const enabledCount = activeTenants.filter(t => {
                      const v = (t.modules || {})[m.key];
                      return v !== undefined ? v === true : MODULE_DEFAULTS[m.key] === true;
                    }).length;
                    const pct = activeTenants.length > 0 ? Math.round((enabledCount / activeTenants.length) * 100) : 0;
                    const Icon = m.icon;
                    return (
                      <div key={m.key} className="flex flex-col items-center gap-2 p-3 rounded-lg border bg-background text-center">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon size={15} className="text-primary" />
                        </div>
                        <p className="text-[11px] font-medium leading-tight">{m.label}</p>
                        <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{enabledCount}</p>
                        <p className="text-[10px] text-muted-foreground">/{activeTenants.length} tenant</p>
                        <div className="w-full bg-muted rounded-full h-1.5">
                          <div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── TENANTS TAB ── */}
        <TabsContent value="tenants">
          <Card>
            <CardContent className="p-0">
              <div className="rounded-lg overflow-hidden divide-y divide-border">
                {/* Header */}
                <div className="grid bg-muted/50 px-4 py-2.5 text-xs font-medium text-muted-foreground"
                  style={{ gridTemplateColumns: '1fr 80px 60px 60px 60px 80px 48px' }}>
                  <span>Azienda</span>
                  <span className="hidden md:block">Piano</span>
                  <span className="hidden lg:block text-center">Utenti</span>
                  <span className="hidden lg:block text-center">Aziende</span>
                  <span className="hidden lg:block text-center">Contatti</span>
                  <span>Stato</span>
                  <span className="text-right">Attivo</span>
                </div>

                {tenants.map(t => {
                  const id = t._id || t.id;
                  const isExpanded = expandedTenant === id;
                  return (
                    <div key={id} data-testid={`tenant-row-${id}`}>
                      {/* Tenant row */}
                      <div className={`grid items-center px-4 py-3 hover:bg-muted/20 transition-colors cursor-pointer ${isExpanded ? 'bg-muted/30' : ''}`}
                        style={{ gridTemplateColumns: '1fr 80px 60px 60px 60px 80px 48px' }}
                        onClick={() => toggleExpand(id)}>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                            <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{t.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{t.slug}</p>
                          </div>
                        </div>
                        <div className="hidden md:block" onClick={e => e.stopPropagation()}>
                          <Badge variant="outline" className="text-xs capitalize">{t.plan}</Badge>
                        </div>
                        <div className="hidden lg:block text-center text-sm">{t.users_count || 0}</div>
                        <div className="hidden lg:block text-center text-sm">{t.companies_count || 0}</div>
                        <div className="hidden lg:block text-center text-sm">{t.contacts_count || 0}</div>
                        <div onClick={e => e.stopPropagation()}>
                          <Badge className={`text-xs ${t.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                            {t.is_active ? 'Attivo' : 'Sospeso'}
                          </Badge>
                        </div>
                        <div className="flex justify-end" onClick={e => e.stopPropagation()}>
                          <Switch checked={t.is_active} onCheckedChange={() => toggleTenant(id)}
                            data-testid={`toggle-tenant-${id}`} />
                        </div>
                      </div>

                      {/* Expanded panel: modules + users */}
                      {isExpanded && (
                        <>
                          <TenantModulesSection tenantId={id} initialModules={t.modules || {}} />
                          <TenantUsersPanel tenantId={id} tenantName={t.name} />
                        </>
                      )}
                    </div>
                  );
                })}

                {tenants.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">Nessun tenant</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── PENDING REQUESTS TAB ── */}
        <TabsContent value="pending">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Richieste nuova azienda in attesa
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pending.length === 0 ? (
                <div className="text-center py-12 space-y-2">
                  <CheckCircle2 size={32} className="text-green-500 mx-auto" />
                  <p className="text-muted-foreground text-sm">Nessuna richiesta in attesa</p>
                </div>
              ) : (
                <div className="divide-y">
                  {pending.map(r => (
                    <div key={r.id} className="px-4 py-4 flex items-start justify-between gap-4" data-testid={`pending-row-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{r.name}</p>
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 dark:border-amber-600 dark:text-amber-400">
                            <Clock size={10} className="mr-1" />In attesa
                          </Badge>
                        </div>
                        <div className="mt-1 space-y-0.5">
                          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                            <Mail size={12} />{r.email}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                            <Building2 size={12} />{r.tenant_name}
                            <span className="font-mono text-xs opacity-60">({r.tenant_slug})</span>
                          </p>
                          {r.created_at && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                              <Calendar size={11} />{new Date(r.created_at).toLocaleString('it-IT')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" variant="outline"
                          className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 border-green-300 dark:border-green-700"
                          onClick={() => approveRequest(r.id, r.name)}
                          disabled={processingId === r.id} data-testid={`approve-btn-${r.id}`}>
                          {processingId === r.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
                          Approva
                        </Button>
                        <Button size="sm" variant="outline"
                          className="text-destructive hover:bg-red-50 dark:hover:bg-red-900/20"
                          onClick={() => rejectRequest(r.id, r.name)}
                          disabled={processingId === r.id} data-testid={`reject-btn-${r.id}`}>
                          <XCircle size={14} className="mr-1" />Rifiuta
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── CREATE TENANT DIALOG ── */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nuovo Tenant</DialogTitle></DialogHeader>
          <form onSubmit={createTenant} className="space-y-4 mt-2">
            {/* Tenant info */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dati Azienda</p>
              <div className="space-y-1.5">
                <Label className="text-xs">Nome Azienda *</Label>
                <Input value={form.name} onChange={e => setField('name', e.target.value)} required data-testid="tenant-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Slug *</Label>
                <Input value={form.slug} onChange={e => setField('slug', e.target.value)} required
                  placeholder="nome-azienda" data-testid="tenant-slug-input" />
                <p className="text-xs text-muted-foreground">Identificativo univoco usato per il login (solo lettere e trattini)</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Piano</Label>
                <Select value={form.plan} onValueChange={v => setField('plan', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Admin Iniziale</p>
                <span className="text-xs text-muted-foreground">(opzionale)</span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nome admin</Label>
                <Input value={form.admin_name} onChange={e => setField('admin_name', e.target.value)} placeholder="Mario Rossi" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email admin</Label>
                <Input type="email" value={form.admin_email} onChange={e => setField('admin_email', e.target.value)} placeholder="admin@azienda.it" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Password admin</Label>
                <div className="relative">
                  <Input type={showPwd ? 'text' : 'password'} value={form.admin_password}
                    onChange={e => setField('admin_password', e.target.value)} />
                  <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPwd ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="create-tenant-submit">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea Tenant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
